import { WrapperError, ErrorCodes } from '../types';

/**
 * Creates a standardized wrapper error
 */
export function createError(code: ErrorCodes, message: string, originalError?: any): WrapperError {
  const error = new Error(message) as WrapperError;
  error.code = code;
  error.name = 'WrapperError';
  
  if (originalError) {
    error.details = {
      originalMessage: originalError.message,
      originalStack: originalError.stack,
      originalCode: originalError.code
    };
  }
  
  return error;
}

/**
 * Validiert eine Ethereum-Adresse
 */
export function validateAddress(address: string): boolean {
  if (!address || typeof address !== 'string') {
    return false;
  }
  
  // Ethereum-Adresse Format: 0x + 40 Hex-Zeichen
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Formats an Ethereum address for display
 */
export function formatAddress(address: string, length: number = 6): string {
  if (!validateAddress(address)) {
    return address;
  }
  
  if (address.length <= length * 2 + 2) {
    return address;
  }
  
  return `${address.slice(0, length + 2)}...${address.slice(-length)}`;
}

/**
 * Konvertiert Wei zu Ether
 */
export function weiToEther(wei: string | number): string {
  const weiValue = typeof wei === 'string' ? BigInt(wei) : BigInt(wei);
  const etherValue = Number(weiValue) / Math.pow(10, 18);
  return etherValue.toFixed(6);
}

/**
 * Konvertiert Ether zu Wei
 */
export function etherToWei(ether: string | number): string {
  const etherValue = typeof ether === 'string' ? parseFloat(ether) : ether;
  // For ethers v5 compatible implementation
  const weiValue = BigInt(Math.floor(etherValue * Math.pow(10, 18)));
  return weiValue.toString();
}

/**
 * Validiert eine Transaktions-Hash
 */
export function validateTransactionHash(hash: string): boolean {
  if (!hash || typeof hash !== 'string') {
    return false;
  }
  
  // Transaktions-Hash Format: 0x + 64 Hex-Zeichen
  return /^0x[a-fA-F0-9]{64}$/.test(hash);
}

/**
 * Wartet eine bestimmte Zeit (in Millisekunden)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry mechanism for asynchronous functions
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: Error | undefined;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxAttempts) {
        break;
      }
      
      await delay(delayMs * attempt); // Exponential backoff
    }
  }
  
  throw createError(
    ErrorCodes.NETWORK_ERROR,
    `Error after ${maxAttempts} attempts`,
    lastError || new Error('Unknown error')
  );
}

/**
 * Debounce function for frequent calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return function(this: any, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Throttle function for performance-critical operations
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function(this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Generates a random ID
 */
export function generateId(prefix: string = 'id'): string {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substr(2, 9);
  return `${prefix}_${timestamp}_${randomStr}`;
}

/**
 * Checks if code is running in browser
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Checks if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    if (!isBrowser()) return false;
    
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sichere JSON-Parsing mit Fallback
 */
export function safeJsonParse<T>(jsonString: string, fallback: T): T {
  try {
    return JSON.parse(jsonString);
  } catch {
    return fallback;
  }
}

/**
 * Formats gas values for display
 */
export function formatGas(gas: string | number): string {
  const gasValue = typeof gas === 'string' ? parseInt(gas) : gas;
  
  if (gasValue >= 1000000) {
    return `${(gasValue / 1000000).toFixed(2)}M`;
  } else if (gasValue >= 1000) {
    return `${(gasValue / 1000).toFixed(1)}K`;
  }
  
  return gasValue.toString();
}

/**
 * Validiert eine Chain-ID
 */
export function validateChainId(chainId: number): boolean {
  return Number.isInteger(chainId) && chainId > 0;
}

/**
 * Creates a deep copy of an object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }
  
  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }
  
  if (typeof obj === 'object') {
    const cloned = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  }
  
  return obj;
}

/**
 * Logging utility with different levels
 */
export class Logger {
  private static instance: Logger;
  private debugMode: boolean = false;
  
  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }
  
  debug(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(`[INCENTIV-WRAPPER DEBUG] ${message}`, ...args);
    }
  }
  
  info(message: string, ...args: any[]): void {
    console.info(`[INCENTIV-WRAPPER] ${message}`, ...args);
  }
  
  warn(message: string, ...args: any[]): void {
    console.warn(`[INCENTIV-WRAPPER WARNING] ${message}`, ...args);
  }
  
  error(message: string, error?: any): void {
    console.error(`[INCENTIV-WRAPPER ERROR] ${message}`, error);
  }
}
